import { queryOptions, useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import type { operations } from '@molchanovs/api-contract';
import { api } from '@shared/api';
import { createQuery } from '@shared/lib/query-helpers';

type MembershipTypesResponse =
  operations['getApiV1MembershipTypes']['responses'][200]['content']['application/json'];
type TrainingProgramsResponse =
  operations['getApiV1TrainingPrograms']['responses'][200]['content']['application/json'];
type TrainingProgramProjection = Omit<
  TrainingProgramsResponse['data']['training_programs'][number],
  'training_program_id' | 'available' | 'type' | 'image' | 'thumbnail' | 'media'
>;
type TrainingProgramsPage = Omit<TrainingProgramsResponse, 'data'> & {
  data: { training_programs: TrainingProgramProjection[] };
};
type PaymentReferenceDataResponse =
  operations['getApiV1PaymentsReferenceData']['responses'][200]['content']['application/json'];
type TrainingProgramOptionsData = Pick<TrainingProgramsPage, 'data'>;

const TRAINING_PROGRAM_PAGE_SIZE = 100;
const MAX_TRAINING_PROGRAM_PAGES = 100;
const trainingProgramPageSchema: z.ZodType<TrainingProgramsPage> = z.object({
  data: z.object({
    training_programs: z
      .array(
        z.object({
          trainingProgramId: z.uuid(),
          revenueCatProductId: z.string().min(1).max(255).nullable().default(null),
          title: z.string(),
          description: z.string().nullable(),
          categories: z.array(z.string()),
          typeLevel: z.enum(['free', 'premium']).nullable(),
          programType: z.enum(['breathing', 'freediving']).nullable(),
          instructors: z.array(
            z.object({
              id: z.uuid(),
              displayName: z.string().min(1),
              avatarUrl: z.url().nullable(),
              profileType: z.literal('instructor'),
            })
          ),
          memberPriceUsdMobile: z.number().nullable(),
          nonMemberPriceUsdMobile: z.number().nullable(),
          videoCover: z.string().nullable(),
          videoUrl: z.url().nullable(),
          videoSource: z.enum(['file', 'external']).nullable(),
          freePreview: z.object({ moduleId: z.uuid(), slideId: z.uuid() }).nullable(),
          status: z.string().nullable(),
          cover: z.string().nullable(),
        })
      )
      .max(TRAINING_PROGRAM_PAGE_SIZE),
  }),
  meta: z.object({
    page: z.number().int().positive(),
    pageSize: z.number().int().positive().max(TRAINING_PROGRAM_PAGE_SIZE),
    totalItems: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
  }),
});

type InvoiceFilterOption = { label: string; value: string };

const catalogueQueryDefaults = {
  gcTime: 5 * 60 * 1_000,
  refetchOnWindowFocus: false,
  retry: false,
  staleTime: 30_000,
} as const;

function sortAndDeduplicateOptions(options: readonly InvoiceFilterOption[]) {
  const byValue = new Map<string, InvoiceFilterOption>();
  for (const option of options) {
    if (!byValue.has(option.value)) byValue.set(option.value, option);
  }
  return [...byValue.values()].sort((left, right) => left.label.localeCompare(right.label));
}

export function toInvoiceMembershipTypeOptions(
  response: MembershipTypesResponse | undefined
): InvoiceFilterOption[] {
  return sortAndDeduplicateOptions(
    (response?.data.membershipTypes ?? []).flatMap(({ id, name }) => {
      const label = name?.trim();
      return label ? [{ label, value: id }] : [];
    })
  );
}

export function toInvoiceTrainingProgramOptions(
  response: TrainingProgramOptionsData | undefined
): InvoiceFilterOption[] {
  if (!response) return [];
  return sortAndDeduplicateOptions(
    response.data.training_programs.map(({ title, trainingProgramId: id }) => ({
      label: title,
      value: id,
    }))
  );
}

export function toInvoicePaymentOptions(
  options: readonly { code: string; label: string }[] | undefined
): InvoiceFilterOption[] {
  return sortAndDeduplicateOptions(
    (options ?? []).map(({ code, label }) => ({ label: label.trim(), value: code }))
  );
}

export const invoiceFilterCatalogueKeys = {
  all: ['invoice-filter-catalogues'] as const,
  membershipTypes: (viewerAccessScope: string) =>
    [...invoiceFilterCatalogueKeys.all, 'membership-types', viewerAccessScope] as const,
  paymentReferenceData: (viewerAccessScope: string) =>
    [...invoiceFilterCatalogueKeys.all, 'payment-reference-data', viewerAccessScope] as const,
  trainingPrograms: (viewerAccessScope: string) =>
    [...invoiceFilterCatalogueKeys.all, 'training-programs', viewerAccessScope] as const,
};

export function invoiceMembershipTypesQuery(viewerAccessScope: string) {
  return createQuery<MembershipTypesResponse>({
    path: '/api/v1/membership-types',
    queryKey: invoiceFilterCatalogueKeys.membershipTypes(viewerAccessScope),
  });
}

export function invoicePaymentReferenceDataQuery(viewerAccessScope: string) {
  return createQuery<PaymentReferenceDataResponse>({
    path: '/api/v1/payments/reference-data',
    queryKey: invoiceFilterCatalogueKeys.paymentReferenceData(viewerAccessScope),
  });
}

export function invoiceTrainingProgramsQuery(viewerAccessScope: string) {
  return queryOptions({
    queryKey: invoiceFilterCatalogueKeys.trainingPrograms(viewerAccessScope),
    queryFn: async ({ signal }): Promise<TrainingProgramOptionsData> => {
      const programs: TrainingProgramProjection[] = [];
      let pageNumber = 1;
      for (let pageIndex = 0; pageIndex < MAX_TRAINING_PROGRAM_PAGES; pageIndex += 1) {
        signal.throwIfAborted();
        const page = await api.get('/api/v1/training-programs', {
          params: { page: pageNumber, pageSize: TRAINING_PROGRAM_PAGE_SIZE },
          signal,
          schema: trainingProgramPageSchema,
        });
        if (page.meta.page !== pageNumber || page.meta.pageSize !== TRAINING_PROGRAM_PAGE_SIZE) {
          throw new Error('Training Program catalog returned invalid numbered-page metadata.');
        }
        programs.push(...page.data.training_programs);
        if (pageNumber >= page.meta.totalPages) return { data: { training_programs: programs } };
        pageNumber += 1;
      }
      throw new Error('Training Program catalog exceeded the invoice lookup page limit.');
    },
  });
}

export function useInvoiceFilterCatalogues(viewerAccessScope: string, enabled = true) {
  const queryEnabled = enabled && viewerAccessScope.length > 0;
  const membershipTypes = useQuery({
    ...invoiceMembershipTypesQuery(viewerAccessScope),
    ...catalogueQueryDefaults,
    enabled: queryEnabled,
  });
  const paymentReferenceData = useQuery({
    ...invoicePaymentReferenceDataQuery(viewerAccessScope),
    ...catalogueQueryDefaults,
    enabled: queryEnabled,
  });
  const trainingPrograms = useQuery({
    ...invoiceTrainingProgramsQuery(viewerAccessScope),
    ...catalogueQueryDefaults,
    enabled: queryEnabled,
  });

  return {
    itemTypes: {
      isError: paymentReferenceData.isError,
      isLoading: paymentReferenceData.isLoading,
      options: toInvoicePaymentOptions(paymentReferenceData.data?.data.itemTypes),
      retry: () => void paymentReferenceData.refetch(),
    },
    membershipTypes: {
      isError: membershipTypes.isError,
      isLoading: membershipTypes.isLoading,
      options: toInvoiceMembershipTypeOptions(membershipTypes.data),
      retry: () => void membershipTypes.refetch(),
    },
    origins: {
      isError: paymentReferenceData.isError,
      isLoading: paymentReferenceData.isLoading,
      options: toInvoicePaymentOptions(paymentReferenceData.data?.data.origins),
      retry: () => void paymentReferenceData.refetch(),
    },
    paymentTypes: {
      isError: paymentReferenceData.isError,
      isLoading: paymentReferenceData.isLoading,
      options: toInvoicePaymentOptions(paymentReferenceData.data?.data.paymentTypes),
      retry: () => void paymentReferenceData.refetch(),
    },
    trainingPrograms: {
      isError: trainingPrograms.isError,
      isLoading: trainingPrograms.isLoading,
      options: toInvoiceTrainingProgramOptions(trainingPrograms.data),
      retry: () => void trainingPrograms.refetch(),
    },
  };
}

export type InvoiceFilterCatalogues = ReturnType<typeof useInvoiceFilterCatalogues>;
